import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, ShieldCheck, Clock, HardHat } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIER_ORDER = ['economy', 'standard', 'premium'];

export default function ProposalView() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState('');
  const [choosing, setChoosing] = useState(null);
  const [clientName, setClientName] = useState('');
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/proposals/public/${token}`)
      .then(r => {
        setProposal(r.data.proposal);
        if (['accepted', 'declined'].includes(r.data.proposal.status)) setResponded(r.data.proposal.status);
        if (r.data.proposal.client_name) setClientName(r.data.proposal.client_name);
      })
      .catch(() => setError('This proposal link is invalid or has been removed.'));
  }, [token]);

  const respond = async (action, tier) => {
    setResponding(true);
    try {
      const r = await axios.post(`${API}/proposals/public/${token}/respond`, {
        action, tier, client_name: clientName || null,
      });
      setResponded(r.data.status);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit your response.');
      setTimeout(() => setError(''), 4000);
    }
    setResponding(false);
  };

  if (error && !proposal) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-100 p-6">
      <p className="font-bold text-neutral-600" data-testid="proposal-error">{error}</p>
    </div>
  );
  if (!proposal) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-100">
      <div className="w-8 h-8 border-4 border-[#2B4C6F] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const tiers = proposal.tiers || {};

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-[#0B1120] pb-16" data-testid="proposal-page">
      <div className="bg-[#2B4C6F] text-white py-10 px-4 text-center border-b-4 border-black">
        <p className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-2 flex items-center justify-center gap-1">
          <HardHat size={14} weight="fill" /> Project Proposal
        </p>
        <h1 className="text-3xl sm:text-4xl font-black uppercase">{(proposal.job_type || 'Your Project').replace(/_/g, ' ')}</h1>
        <p className="mt-2 text-blue-100">
          Prepared by <b>{proposal.contractor_name}</b>{proposal.company ? ` — ${proposal.company}` : ''}{proposal.location ? ` · ${proposal.location}` : ''}
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-6">
        {proposal.note && (
          <div className="bg-amber-50 border-2 border-black p-4 shadow-[4px_4px_0px_#000] mb-6" data-testid="proposal-note-display">
            <p className="text-sm italic">"{proposal.note}"</p>
            <p className="text-xs font-bold mt-1">— {proposal.contractor_name}</p>
          </div>
        )}

        {responded ? (
          <div className="bg-white dark:bg-[#0F172A] border-2 border-black p-8 shadow-[6px_6px_0px_#000] text-center" data-testid="proposal-responded">
            {responded === 'accepted' ? (
              <>
                <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto mb-3" />
                <h2 className="text-2xl font-black uppercase mb-2">Proposal Accepted!</h2>
                <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                  {proposal.contractor_name} has been notified and will reach out to schedule the work.
                </p>
                <p className="text-sm text-neutral-500 mb-4">
                  Want payment protection? Create a free ConstructHelp account and fund the project with escrow —
                  money is only released as milestones are completed.
                </p>
                <Button onClick={() => { window.location.href = '/signup'; }}
                  className="bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase border-2 border-black rounded-none shadow-[3px_3px_0px_#000]"
                  data-testid="proposal-signup-cta">
                  <ShieldCheck size={18} weight="bold" className="mr-2" /> Protect My Payment
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-black uppercase mb-2">Response Sent</h2>
                <p className="text-neutral-600 dark:text-neutral-300">
                  {proposal.contractor_name} has been notified of your decision.
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-4">
              {TIER_ORDER.map(key => {
                const t = tiers[key];
                if (!t) return null;
                const highlighted = key === 'standard';
                return (
                  <div key={key}
                    className={`bg-white dark:bg-[#0F172A] border-2 border-black shadow-[4px_4px_0px_#000] p-5 flex flex-col ${highlighted ? 'ring-4 ring-amber-400' : ''}`}
                    data-testid={`proposal-tier-${key}`}>
                    {highlighted && <span className="self-start text-[10px] font-black uppercase bg-amber-400 border border-black px-2 py-0.5 mb-2">Most Popular</span>}
                    <h3 className="font-black uppercase">{t.name || key}</h3>
                    <p className="text-3xl font-black my-2">${(t.total || 0).toLocaleString()}</p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 flex-1">{t.description}</p>
                    <div className="text-xs text-neutral-500 mt-3 space-y-1">
                      {t.materials_quality && <p>Materials: {t.materials_quality}</p>}
                      {t.timeline_days ? <p className="flex items-center gap-1"><Clock size={12} /> ~{t.timeline_days} days</p> : null}
                      {t.warranty && <p>Warranty: {t.warranty}</p>}
                    </div>
                    <Button onClick={() => setChoosing(key)} disabled={responding}
                      className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase border-2 border-black rounded-none"
                      data-testid={`proposal-accept-${key}`}>
                      Accept {t.name || key}
                    </Button>
                  </div>
                );
              })}
            </div>

            {choosing && (
              <div className="mt-6 bg-white dark:bg-[#0F172A] border-2 border-black p-5 shadow-[4px_4px_0px_#000]" data-testid="proposal-confirm-panel">
                <p className="font-bold mb-2">Confirm: accept the <span className="uppercase">{tiers[choosing]?.name || choosing}</span> option for ${(tiers[choosing]?.total || 0).toLocaleString()}?</p>
                <input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Your name"
                  className="w-full sm:w-72 border-2 border-black p-2 text-sm mb-3 bg-white dark:bg-neutral-800"
                  data-testid="proposal-confirm-name"
                />
                <div className="flex gap-3">
                  <Button onClick={() => respond('accept', choosing)} disabled={responding || !clientName.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase border-2 border-black rounded-none"
                    data-testid="proposal-confirm-accept">
                    {responding ? 'Sending…' : 'Yes, Accept'}
                  </Button>
                  <Button variant="outline" onClick={() => setChoosing(null)} className="border-2 border-black rounded-none" data-testid="proposal-confirm-cancel">Back</Button>
                </div>
              </div>
            )}

            <div className="text-center mt-8">
              <button onClick={() => respond('decline', null)} disabled={responding}
                className="text-sm text-neutral-400 underline hover:text-red-500" data-testid="proposal-decline-btn">
                Decline this proposal
              </button>
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            </div>
          </>
        )}

        <p className="text-center text-xs text-neutral-400 mt-10 flex items-center justify-center gap-1">
          <ShieldCheck size={14} /> Powered by ConstructHelp — estimates by AI, verified by pros, payments protected by escrow.
        </p>
      </div>
    </div>
  );
}
